import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, MapPin, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';

// ------- Section content -------
const VISION_POINTS = [
  'Systems continuously observe their environment.',
  'AI agents understand operational context.',
  'Root causes are identified automatically.',
  'Corrective actions are executed autonomously.',
  'Operations improve continuously through learning and adaptation.',
];

const TAGLINE = ['Observe','Understand','Automate','Optimize'];

// ------- Page -------
const About = () => {
  const { open: openLead } = useLeadDialog();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-black min-h-screen" data-testid="about-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 90% 20%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-20">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Company</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">About</span>
            </nav>
            <div className="mt-7 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                  <Building2 className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">About MetaxuOne</span>
                </div>
                <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[76px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                  Building the future of <br />
                  <span className="italic font-light brand-gradient-text">autonomous operations.</span>
                </h1>
                <p className="mt-6 text-[17px] lg:text-[18px] text-neutral-300 leading-relaxed max-w-3xl">
                  MetaxuOne is an AI-native <strong className="text-white font-semibold">Observability</strong> and <strong className="text-white font-semibold">Self-Healing Operations</strong> platform designed to help organizations monitor, understand, automate, and optimize complex business and technology environments in real time.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button
                    onClick={() => openLead({ source: 'about-hero', title: 'Talk to MetaxuOne' })}
                    className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                    data-testid="about-talk-btn"
                  >
                    Talk to us <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Link to="/platform" className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center">
                    Explore the platform
                  </Link>
                </div>
              </div>

              {/* Tagline pillars */}
              <div className="lg:col-span-4">
                <div className="grid grid-cols-2 gap-3">
                  {TAGLINE.map((t, i) => (
                    <div key={t} className="relative rounded-2xl border border-neutral-800 bg-neutral-950 p-5 overflow-hidden">
                      <div aria-hidden className="absolute -top-10 -right-10 w-32 h-32 rounded-full" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.18), transparent 70%)' }} />
                      <div className="text-[10.5px] uppercase tracking-[0.18em] text-blue-400">step {i + 1}</div>
                      <div className="mt-2 text-[22px] font-semibold text-white tracking-tight">{t}.</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO NARRATIVE */}
        <section className="relative bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-7">
                <p className="text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                  Modern enterprises operate across increasingly fragmented ecosystems — cloud platforms, applications, infrastructure, workflows, business systems, and distributed teams. While organizations generate vast amounts of operational data, most still struggle to transform that data into actionable intelligence and automated outcomes.
                </p>
                <p className="mt-5 text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                  MetaxuOne was created to bridge that gap. We combine <span className="text-white">Enterprise Observability</span>, <span className="text-white">Operational Intelligence</span>, <span className="text-white">AI-Powered Agents</span>, and <span className="text-white">Autonomous Remediation</span> into a unified platform that enables organizations to move beyond monitoring toward intelligent, self-healing operations.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { k: 'Disconnected signals', v: 'Contextual intelligence' },
                    { k: 'Reactive monitoring',  v: 'Autonomous remediation' },
                    { k: 'Manual workflows',     v: 'AI-powered agents' },
                    { k: 'Static dashboards',    v: 'Self-healing operations' },
                  ].map((b) => (
                    <div key={b.k} className="rounded-2xl border border-neutral-800 bg-black p-5">
                      <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500">From</div>
                      <div className="mt-1 text-[14px] text-neutral-400 line-through decoration-neutral-700">{b.k}</div>
                      <div className="mt-3 text-[11px] uppercase tracking-[0.16em] text-blue-400">To</div>
                      <div className="mt-1 text-[14px] font-semibold text-white">{b.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MISSION */}
        <section className="relative bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(40% 50% at 50% 30%, rgba(59,130,246,0.15), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 text-center">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Our mission</p>
            <h2 className="mt-3 text-[32px] sm:text-[42px] lg:text-[56px] leading-[1.05] tracking-[-0.025em] font-semibold text-white max-w-4xl mx-auto">
              To empower organizations with intelligent systems that can <span className="italic font-light brand-gradient-text">observe, understand, decide, and act autonomously</span> — enabling more resilient, efficient, and adaptive operations.
            </h2>
            <p className="mt-7 text-[16px] text-neutral-400 max-w-3xl mx-auto leading-relaxed">
              We believe the future of enterprise operations lies not in managing more dashboards, alerts, and manual workflows — but in creating systems that continuously learn, optimize, and improve themselves.
            </p>
          </div>
        </section>

        {/* WHAT WE DO — hidden per request */}

        {/* OUR PLATFORM (+ embedded ContextModelClip) — hidden per request */}

        {/* WHY METAXUONE — hidden per request */}

        {/* OUR VISION */}
        <section className="relative bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(50% 60% at 80% 30%, rgba(59,130,246,0.15), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
              <div className="lg:col-span-6">
                <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Our vision</p>
                <h2 className="mt-3 text-[34px] sm:text-[42px] lg:text-[50px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  Enterprises that <span className="italic font-light brand-gradient-text">manage themselves</span> — intelligently.
                </h2>
                <p className="mt-6 text-[16px] text-neutral-300 leading-relaxed max-w-xl">
                  We envision a future where enterprise systems are capable of managing themselves intelligently. MetaxuOne is building the foundation for this next generation of intelligent enterprise operations.
                </p>
              </div>
              <div className="lg:col-span-6">
                <ol className="space-y-3">
                  {VISION_POINTS.map((v, i) => (
                    <li key={v} className="flex items-start gap-4 rounded-2xl border border-neutral-800 bg-neutral-950 p-5">
                      <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-300 flex items-center justify-center font-semibold text-[13px] flex-shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <p className="text-[14.5px] text-neutral-200 leading-relaxed">{v}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* WHO WE SERVE — hidden per request */}

        {/* COMMITMENT */}
        <section className="relative bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(50% 60% at 20% 30%, rgba(59,130,246,0.16), transparent), radial-gradient(50% 60% at 80% 70%, rgba(255,255,255,0.04), transparent)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28 text-center">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Our commitment</p>
            <p className="mt-5 text-[16px] text-neutral-300 max-w-3xl mx-auto leading-relaxed">
              We are committed to delivering secure, scalable, and responsible AI-powered operational intelligence solutions that help organizations achieve greater resilience, efficiency, and operational excellence. As technology ecosystems continue to grow in complexity, MetaxuOne remains focused on a single objective:
            </p>
            <h2 className="mt-8 text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.1] tracking-[-0.02em] font-semibold text-white max-w-4xl mx-auto">
              Transforming Enterprise Observability into <br />
              <span className="italic font-light brand-gradient-text">Autonomous, Self-Healing Operations.</span>
            </h2>
          </div>
        </section>

        {/* IDENTITY + FINAL CTA */}
        <section className="relative bg-neutral-950">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="rounded-2xl border border-neutral-800 bg-black p-7">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-blue-300" />
                    <span className="text-[13px] uppercase tracking-[0.18em] text-neutral-400">Company</span>
                  </div>
                  <p className="mt-4 text-[22px] sm:text-[26px] font-semibold text-white">MetaxuOne Private Limited</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-[14px] text-neutral-400">
                    <MapPin className="w-3.5 h-3.5 text-neutral-500" /> Mumbai, Maharashtra, India
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {TAGLINE.map((t) => (
                      <span key={t} className="text-[12.5px] px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 font-medium">{t}.</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 text-left lg:text-right">
                <h3 className="text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  The future of <span className="italic font-light brand-gradient-text">autonomous operations</span> <br /> starts here.
                </h3>
                <div className="mt-6 flex flex-wrap lg:justify-end gap-3">
                  <Button
                    onClick={() => openLead({ source: 'about-bottom', title: 'Talk to MetaxuOne' })}
                    className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                    data-testid="about-bottom-talk-btn"
                  >
                    Talk to us <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Link to="/platform" className="h-12 px-6 rounded-full border border-neutral-700 bg-transparent text-white hover:bg-neutral-900 text-[14px] font-medium inline-flex items-center">
                    Explore the platform
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
